import { generateUUID } from '@/lib/utils';
import {
  BasePipeline,
  DereferencedPipeline,
  DereferencedPipelineOperation,
  FunnelStep,
  InvalidOperation,
  JoinPipelineOperation,
  JoinPipelineStep,
  Pipeline,
  PipelineOperation,
  PipelineWithParent,
} from '@/explore/types';

export const generatePipelineId = generateUUID;

export const isBasePipeline = (pipeline: Pipeline): pipeline is BasePipeline =>
  'baseModelId' in pipeline;

export const isPipelineWithParent = (pipeline: Pipeline): pipeline is PipelineWithParent =>
  'parentId' in pipeline;

export const isPipelineChildOf = (pipeline: Pipeline, potentialParent: Pipeline) =>
  isPipelineWithParent(pipeline) && pipeline.parentId === potentialParent.pipelineId;

export const isPipelineParentOf = (pipeline: Pipeline, potentialChild: Pipeline) =>
  isPipelineWithParent(potentialChild) && potentialChild.parentId === pipeline.pipelineId;

export const isCombinePipeline = (pipeline: Pipeline) =>
  pipeline.operations.some(({ operation }) => operation === 'joinPipeline');

export const createPipeline = (pipeline: Pipeline) =>
  isBasePipeline(pipeline) ? createBasePipeline(pipeline) : createPipelineWithParent(pipeline);

export const createBasePipeline = ({
  baseModelId,
  operations,
}: {
  baseModelId: string;
  operations: PipelineOperation[];
}): BasePipeline => ({
  pipelineId: generatePipelineId(),
  baseModelId,
  operations,
});

export const createPipelineWithParent = ({
  parentId,
  operations,
}: {
  parentId: string;
  operations: PipelineOperation[];
}): PipelineWithParent => ({
  pipelineId: generatePipelineId(),
  parentId,
  operations,
});

export const createDereferencedPipeline = ({
  baseModelId,
  operations,
}: {
  baseModelId: string;
  operations: DereferencedPipelineOperation[];
}): DereferencedPipeline => ({
  baseModelId,
  operations,
});

const ensureOperationOrder = (operations: PipelineOperation[]): PipelineOperation[] => {
  // A hack to avoid "unflattenable pipelines" on a type-level
  const joinPipelineOperationIndex = operations.findIndex(
    (operation) => operation.operation === 'joinPipeline',
  );
  return operations.slice(joinPipelineOperationIndex === -1 ? 0 : joinPipelineOperationIndex);
};

// Makes sure that pipeline does not reference parentPipeline by replacing references to parentPipeline
// either from parentId or any of pipeline operations.
export const removeReferenceToParentPipeline = (
  pipeline: Pipeline,
  parentPipeline: Pipeline,
): Pipeline => {
  const operations = pipeline.operations.map((operation) => {
    if (operation.operation !== 'joinPipeline' || parentPipeline.operations.length > 0) {
      return operation;
    }

    return removeJoinPipelineOperationReferenceToPipeline(operation, parentPipeline);
  });

  if (isPipelineChildOf(pipeline, parentPipeline)) {
    return {
      pipelineId: pipeline.pipelineId,
      ...(isPipelineWithParent(parentPipeline)
        ? { parentId: parentPipeline.parentId }
        : { baseModelId: parentPipeline.baseModelId }),
      operations: [...parentPipeline.operations, ...operations],
    };
  }

  return { ...pipeline, operations: ensureOperationOrder(operations) };
};

const removeJoinPipelineOperationReferenceToPipeline = (
  operation: JoinPipelineOperation,
  pipeline: Pipeline,
): JoinPipelineOperation => {
  return {
    ...operation,
    parameters: {
      ...operation.parameters,
      steps: operation.parameters.steps.map((step) => {
        if (isPipelineChildOf(step.pipeline, pipeline)) {
          return { ...step, pipeline: removeReferenceToParentPipeline(step.pipeline, pipeline) };
        }
        return step;
      }),
    },
  };
};

export const extractSubpipelines = (
  pipeline: DereferencedPipeline,
): {
  pipeline: BasePipeline | PipelineWithParent;
  subPipelines: (BasePipeline | PipelineWithParent)[];
} => {
  const { operations, subPipelines } = pipeline.operations.reduce(
    (acc, operation) => {
      const { operation: updatedOperation, subPipelines } = extractOperationSubPipeline(operation);
      return {
        operations: [...acc.operations, updatedOperation],
        subPipelines: [...acc.subPipelines, ...subPipelines],
      };
    },
    {
      operations: [] as PipelineOperation[],
      subPipelines: [] as (BasePipeline | PipelineWithParent)[],
    },
  );

  const joinPipelineIndex = operations.findIndex((op) => op.operation === 'joinPipeline');
  if (joinPipelineIndex !== -1) {
    const parentOperations = operations.slice(0, joinPipelineIndex);
    const parentPipeline = createBasePipeline({
      baseModelId: pipeline.baseModelId,
      operations: parentOperations,
    });
    return {
      pipeline: createPipelineWithParent({
        parentId: parentPipeline.pipelineId,
        operations: operations.slice(joinPipelineIndex),
      }),
      subPipelines: [parentPipeline, ...subPipelines],
    };
  }

  return {
    pipeline: {
      ...pipeline,
      pipelineId: generatePipelineId(),
      operations,
    },
    subPipelines,
  };
};

const extractOperationSubPipeline = (
  operation: DereferencedPipelineOperation,
): { operation: PipelineOperation; subPipelines: (BasePipeline | PipelineWithParent)[] } => {
  switch (operation.operation) {
    case 'addRelatedColumn': {
      if (
        operation.parameters.pipeline === undefined ||
        operation.parameters.pipeline.operations.length === 0
      ) {
        return { operation, subPipelines: [] };
      }
      const { pipeline, subPipelines } = extractSubpipelines(operation.parameters.pipeline);
      const { pipeline: _, ...parameters } = operation.parameters;
      return {
        operation: {
          ...operation,
          parameters: {
            ...parameters,
            pipelineId: pipeline.pipelineId,
          },
        },
        subPipelines: [pipeline, ...subPipelines],
      };
    }
    case 'relationAggregate':
      {
        const { pipeline, subPipelines } =
          operation.parameters.pipeline !== undefined &&
          operation.parameters.pipeline.operations.length > 0
            ? extractSubpipelines(operation.parameters.pipeline)
            : { pipeline: null, subPipelines: [] };

        if ('relation' in operation.parameters && operation.parameters.relation !== undefined) {
          const { pipeline: _, ...parameters } = operation.parameters;
          return {
            operation: {
              ...operation,
              parameters: {
                ...parameters,
                ...(pipeline !== null ? { pipelineId: pipeline.pipelineId } : {}),
              },
            },
            subPipelines: [...(pipeline !== null ? [pipeline] : []), ...subPipelines],
          };
        } else if (
          'joinStrategy' in operation.parameters &&
          operation.parameters.joinStrategy !== undefined &&
          pipeline !== null
        ) {
          const { pipeline: _, ...parameters } = operation.parameters;
          return {
            operation: {
              ...operation,
              parameters: {
                ...parameters,
                pipelineId: pipeline.pipelineId,
              },
            },
            subPipelines: [pipeline, ...subPipelines],
          };
        }
      }
      throw new Error('Invalid relationAggregate operation parameters');
    case 'joinPipeline': {
      if (!('steps' in operation.parameters)) {
        throw new Error('Invalid joinPipeline operation parameters');
      }
      const { steps, subPipelines } = operation.parameters.steps.reduce(
        (acc, step) => {
          if (step.pipeline.operations.length === 0) {
            return {
              steps: [
                ...acc.steps,
                {
                  ...step,
                  pipeline: { ...step.pipeline, operations: [], pipelineId: generatePipelineId() },
                },
              ],
              subPipelines: acc.subPipelines,
            };
          }
          const { pipeline, subPipelines } = extractSubpipelines(step.pipeline);
          return {
            steps: [
              ...acc.steps,
              {
                ...step,
                pipeline: createPipelineWithParent({
                  parentId: pipeline.pipelineId,
                  operations: [],
                }),
              },
            ],
            subPipelines: [...acc.subPipelines, pipeline, ...subPipelines],
          };
        },
        {
          steps: [] as JoinPipelineStep[],
          subPipelines: [] as (BasePipeline | PipelineWithParent)[],
        },
      );
      return {
        operation: {
          ...operation,
          parameters: {
            ...operation.parameters,
            steps,
          },
        },
        subPipelines,
      };
    }
    case 'funnel': {
      if (!('steps' in operation.parameters)) {
        throw new Error('Invalid funnel operation parameters');
      }
      const { steps, subPipelines } = operation.parameters.steps.reduce(
        (acc, step) => {
          const { pipeline, subPipelines } = extractSubpipelines({
            baseModelId: step.modelId,
            operations: step.operations,
          });
          return {
            steps: [
              ...acc.steps,
              {
                ...step,
                pipeline: createPipelineWithParent({
                  parentId: pipeline.pipelineId,
                  operations: [],
                }),
              },
            ],
            subPipelines: [...acc.subPipelines, pipeline, ...subPipelines],
          };
        },
        { steps: [] as FunnelStep[], subPipelines: [] as (BasePipeline | PipelineWithParent)[] },
      );
      return {
        operation: {
          ...operation,
          parameters: {
            ...operation.parameters,
            steps,
          },
        },
        subPipelines,
      };
    }
    case 'cohort': {
      const { pipeline, subPipelines } = extractSubpipelines(operation.parameters.pipeline);
      return {
        operation: {
          ...operation,
          parameters: {
            ...operation.parameters,
            pipeline: createPipelineWithParent({
              parentId: pipeline.pipelineId,
              operations: [],
            }),
          },
        },
        subPipelines: [pipeline, ...subPipelines],
      };
    }
    case 'invalid':
      return { operation: operation as InvalidOperation, subPipelines: [] };
    case 'noOp':
      throw new Error('Invalid operation in dereferenced pipeline');
    default:
      return { operation, subPipelines: [] };
  }
};
